/**
 * R78 · Excel-like packages editor with drag-to-reorder, section grouping,
 * inline effort-warning icons, and gridlines throughout.
 *
 * A "line" belongs to at most one section (by `section_id`). Rows within a
 * section are ordered by `sort_order`. Ungrouped rows appear last under the
 * synthetic "Ungrouped" section. Section headers get a colored bar and can
 * be renamed / deleted from the section-management strip above the table.
 *
 * Effort warning:
 *   - Only fires if the line links to an activity type that carries a
 *     `default_effort_hours` baseline (>0). If the rolled-up or manually
 *     entered effort deviates ≥50% either way, a small triangle icon shows.
 *
 * Skill breakdown roll-up:
 *   - Adding ≥1 skill row with a designation makes `effort_hours` = sum of
 *     skill hours (readonly, `AUTO` badge). Emptying skills restores manual.
 */
import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  DndContext, pointerWithin, PointerSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  SortableContext, useSortable, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  ChevronDown, ChevronRight, Trash2, Plus, GripVertical,
  AlertTriangle, ChevronsUpDown, ChevronsDownUp, RotateCw,
} from "lucide-react";
import { FREQ_LABELS, CATEGORIES as BUILTIN_CATEGORIES, fmtMoney } from "@/pages/app/Packages";
import { ChannelPicker } from "./channels";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";

export function sumSkillHours(skills) {
  if (!Array.isArray(skills)) return 0;
  return skills.reduce((s, r) => s + (Number(r?.hours) || 0), 0);
}

const NO_SECTION_ID = "__nosection__";

/**
 * Compute effort warning severity by comparing `effort_hours` to a baseline
 * `default_effort_hours` on the linked activity type.
 * Returns { level: 'none'|'low'|'high'|'strong', msg } or null when no
 * baseline is available.
 */
function effortWarning(line, activityIndex, tolerancePct = 50) {
  const aid = line.activity_type_id;
  if (!aid) return null;
  const act = activityIndex.get(aid);
  const baseline = Number(act?.default_effort_hours || 0);
  if (baseline <= 0) return null;
  const val = Number(line.effort_hours || 0);
  if (val <= 0) return { level: "low", msg: `Baseline is ${baseline}h/u — this row has 0` };
  const tol = Math.max(5, Number(tolerancePct) || 50) / 100;   // 0.5 → ±50%
  const lower = baseline * (1 - tol);
  const upper = baseline * (1 + tol);
  if (val >= baseline * 2)
    return { level: "strong", msg: `2× baseline (${baseline}h/u) — verify pricing` };
  if (val > upper)
    return { level: "high", msg: `${Math.round(val / baseline * 100)}% of baseline (${baseline}h/u), > tolerance ±${Math.round(tol * 100)}%` };
  if (val < lower)
    return { level: "high", msg: `${Math.round(val / baseline * 100)}% of baseline (${baseline}h/u), < tolerance ±${Math.round(tol * 100)}%` };
  return null;
}

export default function PackageLinesTable({
  lines,
  sections,               // [{id,name,order,color}]
  currency,
  designations,
  activityTypes,          // full list to build the effort baseline lookup
  categories,             // R83 · dynamic list; falls back to builtin
  members,                // R84 · [{user_id,name,email,avatar_url}]
  effortTolerancePct = 50,
  showMoney,
  showRates,
  showCosts,
  onUpdate,
  onRemove,
  onReorder,
  onSectionsChange,
  // R268vvv · Feb 2026 — fired when the primary assignee on a line
  // changes so the parent can prompt for a month-range backfill.
  onPrimaryAssigneeChanged,
  // Feb 2026 · Fired when the PM taps the ↻ chip next to a skill row's
  // hours input so the parent can open the skill-hours → tasks backfill
  // dialog.
  onSkillHoursBackfill,
}) {
  // Feb 2026 R98 · Auto-propagate assignee across skill rows sharing the
  // same designation within this package. When a user assigns a person to a
  // designation for the FIRST time in the package (i.e. every other row
  // with that designation is still unassigned), we prompt "Assign the same
  // person to N other rows with this designation?" and, on confirm, patch
  // every matching skill row in one go. Silent no-op when there are no
  // matching rows or when at least one other row is already assigned.
  const memberById = useMemo(() => {
    const m = new Map();
    (members || []).forEach((mem) => { m.set(mem.user_id, mem); });
    return m;
  }, [members]);

  const handleUpdate = (index, patch) => {
    // Fast path — anything that isn't a skill_breakdown edit skips the whole
    // detection block.
    if (!patch || !Object.prototype.hasOwnProperty.call(patch, "skill_breakdown")) {
      onUpdate(index, patch);
      return;
    }
    const prevLine = lines[index] || {};
    const prevSkills = Array.isArray(prevLine.skill_breakdown) ? prevLine.skill_breakdown : [];
    const nextSkills = Array.isArray(patch.skill_breakdown) ? patch.skill_breakdown : [];

    // Find the skill row whose assignee changed from empty → set.
    let newlySet = null; // { designation, assignee_id, skillIdx }
    for (let si = 0; si < nextSkills.length; si++) {
      const nu = nextSkills[si];
      const pu = prevSkills[si] || {};
      const desig = (nu?.designation || "").trim();
      if (!desig) continue;
      const prevAss = pu.assignee_id || null;
      const nextAss = nu?.assignee_id || null;
      if (!prevAss && nextAss) {
        newlySet = { designation: desig, assignee_id: nextAss, skillIdx: si };
        break;
      }
    }
    if (!newlySet) { onUpdate(index, patch); return; }

    // Snapshot: lines with the current patch applied.
    const snapshot = lines.map((l, j) => (j === index ? { ...l, ...patch } : l));

    // Collect every skill row (any line) matching this designation.
    const matches = [];
    for (let li = 0; li < snapshot.length; li++) {
      const skb = Array.isArray(snapshot[li].skill_breakdown) ? snapshot[li].skill_breakdown : [];
      for (let si = 0; si < skb.length; si++) {
        const row = skb[si] || {};
        if ((row.designation || "").trim() === newlySet.designation) {
          matches.push({ li, si, assignee_id: row.assignee_id || null });
        }
      }
    }
    // Only prompt when THIS is the sole assigned row (i.e. first-time assign
    // in the package) AND at least one other unassigned row exists.
    const assignedRows = matches.filter((m) => m.assignee_id);
    const unassignedRows = matches.filter((m) => !m.assignee_id);
    const isFirstAssign =
      assignedRows.length === 1
      && assignedRows[0].li === index
      && assignedRows[0].si === newlySet.skillIdx
      && unassignedRows.length >= 1;

    // Apply the primary update first so the prompt state matches the UI.
    onUpdate(index, patch);
    if (!isFirstAssign) return;

    const mem = memberById.get(newlySet.assignee_id);
    const personLabel = (mem && (mem.name || mem.email)) || "this person";
    const msg = (
      `Assign ${personLabel} to ${unassignedRows.length} other skill row`
      + (unassignedRows.length === 1 ? "" : "s")
      + ` with designation "${newlySet.designation}" in this package?`
    );
    // Defer to next tick so the just-applied setState commits before we
    // prompt (otherwise the user sees the old cell state behind the modal).
    setTimeout(() => {
      // Native confirm — matches the tone of the rest of the packages editor.
      const ok = window.confirm(msg);
      if (!ok) return;
      // Group unassigned matches by line index so we submit one patch per
      // line (avoids interleaved setStates on the same row).
      const byLine = new Map();
      for (const r of unassignedRows) {
        if (!byLine.has(r.li)) byLine.set(r.li, new Set());
        byLine.get(r.li).add(r.si);
      }
      for (const [li, siSet] of byLine.entries()) {
        const base = snapshot[li];
        const skb = Array.isArray(base.skill_breakdown) ? base.skill_breakdown : [];
        const nextSkb = skb.map((row, si) => (
          siSet.has(si) ? { ...row, assignee_id: newlySet.assignee_id } : row
        ));
        onUpdate(li, { skill_breakdown: nextSkb });
      }
    }, 0);
  };
  const catList = useMemo(() => {
    const merged = Array.from(new Set([
      ...BUILTIN_CATEGORIES,
      ...((categories || []).map((c) => (c || "").trim()).filter(Boolean)),
    ]));
    return merged;
  }, [categories]);
  const [expanded, setExpanded] = useState(() => new Set());
  const toggle = (id) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  // R84 · Expand-all / Collapse-all toggle. When *any* line row is collapsed,
  // one tap opens the skill breakdown on every line; a second tap folds
  // everything back. Tracks total lines so future rows also expand.
  const allExpanded = lines.length > 0 && lines.every((l) => expanded.has(l.id));
  const toggleAll = () => {
    setExpanded((prev) => {
      if (prev.size >= lines.length && allExpanded) return new Set();
      return new Set(lines.map((l) => l.id).filter(Boolean));
    });
  };

  const activityIndex = useMemo(() => {
    const m = new Map();
    (activityTypes || []).forEach((a) => m.set(a.id, a));
    return m;
  }, [activityTypes]);

  const sortedSections = useMemo(
    () => [...(sections || [])].sort((a, b) => (a.order || 0) - (b.order || 0)),
    [sections],
  );

  // Group lines by section (preserving stable index for updates).
  const grouped = useMemo(() => {
    const bySection = new Map();
    bySection.set(NO_SECTION_ID, []);
    sortedSections.forEach((s) => bySection.set(s.id, []));
    lines.forEach((l, i) => {
      const sid = l.section_id && bySection.has(l.section_id) ? l.section_id : NO_SECTION_ID;
      bySection.get(sid).push({ line: l, index: i });
    });
    // Sort each bucket by sort_order (falling back to their original index).
    for (const arr of bySection.values()) {
      arr.sort((a, b) => {
        const av = Number(a.line.sort_order ?? a.index * 10);
        const bv = Number(b.line.sort_order ?? b.index * 10);
        return av - bv;
      });
    }
    return bySection;
  }, [lines, sortedSections]);

  const colCount = 10                                         // handle + expand + name + cat + type + freq + count + effort + channels + primary
    + (showRates ? 2 : 0)                                     // rate + amount
    + (showCosts ? 1 : 0)                                     // cost
    + 1;                                                      // trash

  const sensors = useSensors(useSensor(PointerSensor, {
    activationConstraint: { distance: 6 },  // tiny threshold so click on handle actually drags
  }));

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const isSectionActive = String(active.id).startsWith("sec:");
    const isSectionOver = String(over.id).startsWith("sec:");
    // R79 · Reorder sections themselves.
    if (isSectionActive && isSectionOver) {
      const aId = String(active.id).slice(4);
      const oId = String(over.id).slice(4);
      const ordered = [...sortedSections];
      const from = ordered.findIndex((s) => s.id === aId);
      const to = ordered.findIndex((s) => s.id === oId);
      if (from < 0 || to < 0 || from === to) return;
      const [moved] = ordered.splice(from, 1);
      ordered.splice(to, 0, moved);
      // Re-stamp `order` densely so the persisted values match visual order.
      onSectionsChange(ordered.map((s, i) => ({ ...s, order: (i + 1) * 10 })));
      return;
    }
    if (isSectionActive || isSectionOver === undefined) return;

    // Line-row drag. Two flavours:
    //   • drop on another row → slot between neighbours and inherit section
    //   • drop on a section header → append to end of that section
    const activeIdx = lines.findIndex((l) => l.id === active.id);
    if (activeIdx < 0) return;

    let targetSection;
    let anchorOrder;   // sort_order of the row immediately BEFORE the drop
    let afterOrder;    // sort_order of the row immediately AFTER the drop

    if (isSectionOver) {
      // Dropped on a section header → append to end of that section.
      const targetId = String(over.id).slice(4);
      targetSection = targetId === NO_SECTION_ID ? null : targetId;
      const bucket = lines
        .filter((l, i) => (l.section_id ?? null) === (targetSection ?? null) && i !== activeIdx)
        .sort((a, b) => (Number(a.sort_order ?? 0) - Number(b.sort_order ?? 0)));
      anchorOrder = bucket.length ? Number(bucket[bucket.length - 1].sort_order ?? bucket.length * 10) : null;
      afterOrder = null;
    } else {
      const overIdx = lines.findIndex((l) => l.id === over.id);
      if (overIdx < 0) return;
      const target = lines[overIdx];
      targetSection = target.section_id ?? null;
      const bucket = lines
        .map((l, i) => ({ l, i }))
        .filter(({ l, i }) => (l.section_id ?? null) === targetSection && i !== activeIdx)
        .sort((a, b) => (Number(a.l.sort_order ?? a.i * 10)) - (Number(b.l.sort_order ?? b.i * 10)));
      const posInBucket = bucket.findIndex(({ i }) => i === overIdx);
      const before = bucket[posInBucket - 1];
      const after = bucket[posInBucket];
      anchorOrder = before ? Number(before.l.sort_order ?? before.i * 10) : null;
      afterOrder = after ? Number(after.l.sort_order ?? after.i * 10) : null;
    }

    let newOrder;
    if (anchorOrder == null && afterOrder == null) newOrder = 10;
    else if (anchorOrder == null) newOrder = afterOrder - 5;
    else if (afterOrder == null) newOrder = anchorOrder + 5;
    else newOrder = (anchorOrder + afterOrder) / 2;

    const next = lines.map((l, i) => i === activeIdx
      ? { ...l, section_id: targetSection, sort_order: newOrder }
      : l);
    onReorder(next);
  };

  const allSortableIds = useMemo(() => {
    // Order: section header (line rows in that section) ... for each section,
    // then the ungrouped rows. Ids are prefixed `sec:` for headers to keep
    // them distinguishable from line ids in the drag handler.
    const ids = [];
    sortedSections.forEach((s) => {
      ids.push(`sec:${s.id}`);
      (grouped.get(s.id) || []).forEach((it) => ids.push(it.line.id));
    });
    (grouped.get(NO_SECTION_ID) || []).forEach((it) => ids.push(it.line.id));
    return ids;
  }, [sortedSections, grouped]);

  if (lines.length === 0 && sortedSections.length === 0) {
    return (
      <div className="border-2 border-dashed border-border p-8 text-center text-sm text-muted-foreground" data-testid="pkg-lines-empty">
        No deliverable lines yet — add from the activity master or a custom line.
      </div>
    );
  }

  const renderSection = (sec) => {
    const items = grouped.get(sec.id) || [];
    return (
      <SectionBlock
        key={sec.id}
        section={sec}
        items={items}
        activityIndex={activityIndex}
        effortTolerancePct={effortTolerancePct}
        expanded={expanded}
        toggle={toggle}
        currency={currency}
        designations={designations}
        members={members}
        showRates={showRates}
        showCosts={showCosts}
        colCount={colCount}
        onUpdate={onUpdate}
        onRemove={onRemove}
        onRenameSection={(id, name) => onSectionsChange(sections.map((s) => s.id === id ? { ...s, name } : s))}
        onColorSection={(id, color) => onSectionsChange(sections.map((s) => s.id === id ? { ...s, color } : s))}
        onDeleteSection={(id) => {
          const remainingLines = lines.map((l) => l.section_id === id ? { ...l, section_id: null } : l);
          onReorder(remainingLines);
          onSectionsChange(sections.filter((s) => s.id !== id));
        }}
        catList={catList}
        onPrimaryAssigneeChanged={onPrimaryAssigneeChanged}
        onSkillHoursBackfill={onSkillHoursBackfill}
      />
    );
  };

  const ungroupedItems = grouped.get(NO_SECTION_ID) || [];

  return (
    <DndContext sensors={sensors} collisionDetection={pointerWithin} onDragEnd={handleDragEnd}>
      <SortableContext items={allSortableIds} strategy={verticalListSortingStrategy}>
        <div className="border border-border overflow-x-auto" data-testid="pkg-lines-table">
        {/* R268bbbb · Feb 2026 — Bumped `min-w` from 820 → 1180 so the
            Frequency / Count / Effort / Channels / Primary columns
            all have room to breathe, and widened each fixed-width
            column so nothing gets cropped on desktop. The wrapper is
            already `overflow-x-auto` so mobile still gets a scroll
            fallback. */}
        <table className="min-w-[1000px] w-full text-[11px] border-collapse">
          <thead className="bg-muted border-b-2 border-border sticky top-0 z-10 shadow-[0_2px_0_0_hsl(var(--border))]">
            <tr className="text-[10px] uppercase tracking-widest text-muted-foreground">
              <th className="w-5 px-0.5 py-1 border-r border-border bg-muted"></th>
              <th className="w-5 px-0.5 py-1 border-r border-border bg-muted">
                <button
                  type="button"
                  onClick={toggleAll}
                  className="text-muted-foreground hover:text-foreground mx-auto flex items-center justify-center"
                  title={allExpanded ? "Collapse all skill breakdowns" : "Expand all skill breakdowns"}
                  data-testid="pkg-lines-toggle-all"
                >
                  {allExpanded
                    ? <ChevronsDownUp className="w-3.5 h-3.5" />
                    : <ChevronsUpDown className="w-3.5 h-3.5" />}
                </button>
              </th>
              <th className="text-left px-1.5 py-1 border-r border-border bg-muted min-w-[200px]">Deliverable</th>
              <th className="text-left px-1.5 py-1 border-r border-border w-24 bg-muted">Category</th>
              <th className="text-left px-1.5 py-1 border-r border-border w-16 bg-muted">Type</th>
              <th className="text-left px-1.5 py-1 border-r border-border w-24 bg-muted">Frequency</th>
              <th className="text-right px-1.5 py-1 border-r border-border w-16 bg-muted">Count</th>
              <th className="text-right px-1.5 py-1 border-r border-border w-20 bg-muted">Effort/u</th>
              <th className="text-left px-1.5 py-1 border-r border-border w-28 bg-muted">Channels</th>
              <th className="text-left px-1.5 py-1 border-r border-border w-28 bg-muted" title="Primary assignee (owner) that gets baked onto every generated deliverable">Primary</th>
              {showRates && <th className="text-right px-1.5 py-1 border-r border-border w-20 bg-muted">Rate/u</th>}
              {showRates && <th className="text-right px-1.5 py-1 border-r border-border w-24 bg-muted">Amount/mo</th>}
              {showCosts && <th className="text-right px-1.5 py-1 border-r border-border w-24 bg-muted">Cost/mo</th>}
              {!showMoney && <th className="text-right px-1.5 py-1 border-r border-border w-16 bg-muted">/mo</th>}
              <th className="w-5 px-0.5 py-1 bg-muted"></th>
            </tr>
          </thead>
          <tbody>
            {sortedSections.map((sec) => renderSection(sec))}
            {ungroupedItems.length > 0 && (
              <SectionBlock
                key={NO_SECTION_ID}
                section={{ id: NO_SECTION_ID, name: sortedSections.length > 0 ? "Ungrouped" : null, order: 999 }}
                items={ungroupedItems}
                activityIndex={activityIndex}
                effortTolerancePct={effortTolerancePct}
                expanded={expanded}
                toggle={toggle}
                currency={currency}
                designations={designations}
                members={members}
                showRates={showRates}
                showCosts={showCosts}
                colCount={colCount}
                onUpdate={handleUpdate}
                onRemove={onRemove}
                catList={catList}
                onPrimaryAssigneeChanged={onPrimaryAssigneeChanged}
                onSkillHoursBackfill={onSkillHoursBackfill}
              />
            )}
          </tbody>
        </table>
        </div>
      </SortableContext>
    </DndContext>
  );
}

function SectionBlock({
  section, items, activityIndex, effortTolerancePct,
  expanded, toggle,
  currency, designations, members, showRates, showCosts, colCount,
  onUpdate, onRemove, onRenameSection, onDeleteSection, onColorSection,
  catList,
  onPrimaryAssigneeChanged,
  onSkillHoursBackfill,
}) {
  const isReal = section.id !== NO_SECTION_ID;
  const showHeader = isReal || (section.name && items.length > 0);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(section.name || "");
  const submitName = () => {
    setEditing(false);
    const trimmed = (name || "").trim();
    if (trimmed && trimmed !== section.name && onRenameSection) {
      onRenameSection(section.id, trimmed);
    } else {
      setName(section.name || "");
    }
  };

  return (
    <>
      {showHeader && (
        isReal ? (
          <SortableSectionHeader
            section={section}
            colCount={colCount}
            editing={editing}
            name={name}
            setName={setName}
            setEditing={setEditing}
            submitName={submitName}
            itemsCount={items.length}
            onRenameSection={onRenameSection}
            onDeleteSection={onDeleteSection}
            onColorSection={onColorSection}
          />
        ) : (
          // Static "Ungrouped" fallback header — not draggable.
          <tr className="bg-primary/5 border-t border-border border-b border-border" data-testid={`pkg-section-${section.id}`}>
            <td colSpan={colCount} className="px-2 py-1.5">
              <div className="flex items-center gap-2">
                <span className="inline-block w-1 h-4 bg-muted-foreground/40" aria-hidden />
                <span className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">{section.name}</span>
                <span className="text-[10px] text-muted-foreground">{items.length} item{items.length === 1 ? "" : "s"}</span>
              </div>
            </td>
          </tr>
        )
      )}
      {items.map(({ line, index }) => (
        <SortableRow
          key={line.id}
          line={line}
          index={index}
          expanded={expanded.has(line.id)}
          onToggleExpand={() => toggle(line.id)}
          onUpdate={(patch) => onUpdate(index, patch)}
          onRemove={() => onRemove(index)}
          designations={designations}
          members={members}
          activityIndex={activityIndex}
          effortTolerancePct={effortTolerancePct}
          currency={currency}
          showRates={showRates}
          showCosts={showCosts}
          colCount={colCount}
          catList={catList}
          onPrimaryAssigneeChanged={onPrimaryAssigneeChanged}
          onSkillHoursBackfill={onSkillHoursBackfill}
        />
      ))}
    </>
  );
}

function SortableSectionHeader({
  section, colCount, editing, name, setName, setEditing, submitName,
  itemsCount, onRenameSection, onDeleteSection, onColorSection,
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: `sec:${section.id}` });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  const accent = section.color || "hsl(var(--primary))";
  return (
    <tr ref={setNodeRef} style={style} className="border-t border-border border-b border-border" data-testid={`pkg-section-${section.id}`}>
      <td colSpan={colCount} className="px-2 py-2.5" style={{ background: `${accent}18` /* ~10% opacity */ }}>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="cursor-grab active:cursor-grabbing"
            style={{ color: accent, opacity: 0.7 }}
            title="Drag to reorder section (or drop a line here to move it into this section)"
            data-testid={`pkg-section-drag-${section.id}`}
            {...attributes}
            {...listeners}
          >
            <GripVertical className="w-3 h-3" />
          </button>
          <span className="inline-block w-1 h-4" style={{ background: accent }} aria-hidden />
          {editing ? (
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={submitName}
              onKeyDown={(e) => { if (e.key === "Enter") submitName(); if (e.key === "Escape") { setEditing(false); setName(section.name || ""); } }}
              autoFocus
              className="text-[11px] font-bold uppercase tracking-widest bg-transparent outline-none border-b"
              style={{ borderColor: accent }}
              data-testid={`pkg-section-rename-${section.id}`}
            />
          ) : (
            <button
              type="button"
              onClick={() => onRenameSection && setEditing(true)}
              className="text-[11px] font-bold uppercase tracking-widest hover:underline"
              style={{ color: accent }}
              data-testid={`pkg-section-name-${section.id}`}
            >
              {section.name}
            </button>
          )}
          <span className="text-[10px] text-muted-foreground">{itemsCount} item{itemsCount === 1 ? "" : "s"}</span>
          {onColorSection && (
            <SectionColorSwatch
              value={section.color}
              onChange={(hex) => onColorSection(section.id, hex)}
              testid={`pkg-section-color-${section.id}`}
            />
          )}
          {onDeleteSection && (
            <button
              type="button"
              onClick={() => onDeleteSection(section.id)}
              className="ml-auto text-muted-foreground hover:text-destructive"
              title="Remove section (lines will become ungrouped)"
              data-testid={`pkg-section-del-${section.id}`}
            >
              <Trash2 className="w-3 h-3" />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

const PALETTE = [
  "#3B82F6", // blue
  "#10B981", // emerald
  "#F59E0B", // amber
  "#EF4444", // rose
  "#8B5CF6", // violet
  "#EC4899", // pink
  "#14B8A6", // teal
  "#6B7280", // slate
];

function SectionColorSwatch({ value, onChange, testid }) {
  const [open, setOpen] = useState(false);
  const current = value || PALETTE[0];
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-3.5 h-3.5 border border-border/60 rounded-full"
        style={{ background: current }}
        title="Pick a colour for this section"
        data-testid={testid}
      />
      {open && (
        <div
          className="absolute z-40 top-5 left-0 bg-card border border-border shadow-md p-1.5 flex gap-1 flex-wrap w-32"
          data-testid={`${testid}-popover`}
          onMouseLeave={() => setOpen(false)}
        >
          {PALETTE.map((hex) => (
            <button
              key={hex}
              type="button"
              onClick={() => { onChange(hex); setOpen(false); }}
              className="w-4 h-4 border border-border/60 hover:scale-110 transition"
              style={{ background: hex }}
              data-testid={`${testid}-opt-${hex.replace("#", "")}`}
            />
          ))}
          <button
            type="button"
            onClick={() => { onChange(null); setOpen(false); }}
            className="w-4 h-4 border border-border/60 flex items-center justify-center text-[9px] text-muted-foreground"
            title="Reset colour"
            data-testid={`${testid}-reset`}
          >×</button>
        </div>
      )}
    </div>
  );
}

function SortableRow(props) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: props.line.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };
  return <LineRow {...props} dragProps={{ setNodeRef, style, attributes, listeners }} />;
}

function LineRow({
  line, index, expanded, onToggleExpand, onUpdate, onRemove,
  designations, members, activityIndex, effortTolerancePct,
  currency, showRates, showCosts, colCount, catList,
  onPrimaryAssigneeChanged,
  onSkillHoursBackfill,
  dragProps,
}) {
  const skills = Array.isArray(line.skill_breakdown) ? line.skill_breakdown : [];
  const filled = skills.filter((s) => (s?.designation || "").trim().length > 0);
  const autoEffort = filled.length > 0;
  const rolledUp = useMemo(() => sumSkillHours(filled), [filled]);
  const displayedEffort = autoEffort ? rolledUp : (Number(line.effort_hours) || 0);
  const warning = effortWarning({ ...line, effort_hours: displayedEffort }, activityIndex, effortTolerancePct);

  const updateSkills = (nextSkills) => {
    const filledNext = nextSkills.filter((s) => (s?.designation || "").trim().length > 0);
    const patch = { skill_breakdown: nextSkills };
    if (filledNext.length > 0) patch.effort_hours = sumSkillHours(filledNext);
    onUpdate(patch);
  };
  const addSkill = () => updateSkills([...skills, { designation: "", hours: 0, assignee_id: null }]);
  const setSkill = (si, patch) => updateSkills(skills.map((s, j) => (j === si ? { ...s, ...patch } : s)));
  const removeSkill = (si) => updateSkills(skills.filter((_, j) => j !== si));

  return (
    <>
      <tr
        ref={dragProps.setNodeRef}
        style={dragProps.style}
        className="border-b border-border hover:bg-muted/20"
        data-testid={`pkg-line-${index}`}
      >
        <td className="border-r border-border px-1 py-1 align-middle text-center" {...dragProps.attributes} {...dragProps.listeners}>
          <button
            type="button"
            className="text-muted-foreground/70 hover:text-foreground cursor-grab active:cursor-grabbing"
            title="Drag to reorder"
            data-testid={`line-drag-${index}`}
          >
            <GripVertical className="w-3 h-3" />
          </button>
        </td>
        <td className="border-r border-border px-1 py-1 align-middle text-center">
          <button
            type="button"
            onClick={onToggleExpand}
            className="text-muted-foreground hover:text-foreground"
            title={expanded ? "Hide skills" : "Show skills"}
            data-testid={`line-expand-${index}`}
          >
            {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          </button>
        </td>
        <td className="border-r border-border px-1 py-0.5 align-middle">
          <Input
            value={line.name || ""}
            onChange={(e) => onUpdate({ name: e.target.value })}
            className="h-6 rounded-none text-[11px] font-medium border-transparent focus:border-input"
            data-testid={`line-name-${index}`}
          />
          {autoEffort && (
            <span className="ml-1 text-[9px] uppercase tracking-widest bg-amber-100 border border-amber-300 text-amber-800 px-1"
                  title="Effort/u rolls up from skills"
                  data-testid={`line-auto-badge-${index}`}>AUTO</span>
          )}
        </td>
        <td className="border-r border-border px-1 py-0.5 align-middle">
          <Select value={line.category || "other"} onValueChange={(v) => onUpdate({ category: v })}>
            <SelectTrigger className="h-6 rounded-none text-[10px] border-transparent focus:border-input" data-testid={`line-category-${index}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(catList || BUILTIN_CATEGORIES).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </td>
        <td className="border-r border-border px-1 py-0.5 align-middle">
          <Select value={line.is_external ? "external" : "internal"} onValueChange={(v) => onUpdate({ is_external: v === "external" })}>
            <SelectTrigger className="h-6 rounded-none text-[10px] border-transparent focus:border-input" data-testid={`line-type-${index}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="external">external</SelectItem>
              <SelectItem value="internal">internal</SelectItem>
            </SelectContent>
          </Select>
        </td>
        <td className="border-r border-border px-1 py-0.5 align-middle">
          <Select value={line.frequency || "monthly"} onValueChange={(v) => onUpdate({ frequency: v })}>
            <SelectTrigger className="h-6 rounded-none text-[10px] border-transparent focus:border-input" data-testid={`line-freq-${index}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(FREQ_LABELS).map(([k, lab]) => <SelectItem key={k} value={k}>{lab}</SelectItem>)}
            </SelectContent>
          </Select>
        </td>
        <td className="border-r border-border px-1 py-0.5 align-middle text-right">
          <Input
            type="number" min={0.5} step={0.5}
            value={line.count ?? 1}
            onChange={(e) => onUpdate({ count: e.target.value })}
            className="h-6 rounded-none text-[11px] text-right border-transparent focus:border-input"
            data-testid={`line-count-${index}`}
          />
        </td>
        <td className="border-r border-border px-1 py-0.5 align-middle text-right relative">
          <div className="flex items-center gap-1 justify-end">
            {warning && (
              <span
                title={warning.msg}
                className={`inline-flex items-center ${warning.level === "strong" ? "text-rose-600" : "text-amber-600"}`}
                data-testid={`line-effort-warn-${index}`}
                data-warning-level={warning.level}
              >
                <AlertTriangle className="w-3 h-3" />
              </span>
            )}
            <Input
              type="number" min={0} step={0.25}
              value={autoEffort ? rolledUp : (line.effort_hours ?? 0)}
              onChange={(e) => onUpdate({ effort_hours: e.target.value })}
              disabled={autoEffort}
              className={`h-6 rounded-none text-[11px] text-right border-transparent focus:border-input ${autoEffort ? "bg-muted/60 cursor-not-allowed" : ""}`}
              data-testid={`line-effort-${index}`}
              title={autoEffort ? "Rolled up from skill breakdown" : "Manual effort per unit (h)"}
            />
          </div>
        </td>
        <td className="border-r border-border px-1 py-0.5 align-middle">
          <ChannelPicker
            value={line.channels || []}
            onChange={(next) => onUpdate({ channels: next })}
            testid={`line-channels-${index}`}
          />
        </td>
        {/* R268vvv · Feb 2026 — Primary assignee (owner) picker per
            deliverable line. `onUpdate` here writes to local state
            only; the parent flushes to Mongo on package save. On
            change, `onPrimaryAssigneeChanged` (below) fires the
            backfill prompt so the PM can push the new owner onto
            existing generated deliverables.

            Feb 2026 · Switched from a plain <select> to the shared
            SkillAssigneePicker so the picker is searchable and only
            lists ACTIVE workspace members. Legacy inactive assignments
            are preserved — the current value is pinned at the top
            even when inactive. */}
        <td className="border-r border-border px-1 py-0.5 align-middle" data-testid={`line-primary-${index}`}>
          <SkillAssigneePicker
            value={line.primary_assignee_user_id || null}
            members={members}
            onChange={(uid) => {
              const prev = line.primary_assignee_user_id || null;
              onUpdate({ primary_assignee_user_id: uid });
              if (onPrimaryAssigneeChanged && uid !== prev) {
                onPrimaryAssigneeChanged(line, uid, prev);
              }
            }}
            testid={`line-primary-select-${index}`}
          />
        </td>
        {showRates && (
          <td className="border-r border-border px-1 py-0.5 align-middle text-right">
            <Input
              type="number" min={0}
              value={line.rate ?? 0}
              onChange={(e) => onUpdate({ rate: e.target.value })}
              className="h-6 rounded-none text-[11px] text-right border-transparent focus:border-input"
              data-testid={`line-rate-${index}`}
            />
          </td>
        )}
        {showRates && (
          <td className="border-r border-border px-1 py-0.5 align-middle text-right font-mono text-emerald-700 text-[11px]" data-testid={`line-mprice-${index}`}>
            {fmtMoney(line.monthly_price, currency)}
            <div className="text-[9px] text-muted-foreground">{line.monthly_qty ?? "—"} /mo</div>
          </td>
        )}
        {showCosts && (
          <td className="border-r border-border px-1 py-0.5 align-middle text-right font-mono text-muted-foreground text-[11px]" data-testid={`line-mcost-${index}`}>
            {fmtMoney(line.monthly_cost, currency)}
          </td>
        )}
        {!showRates && !showCosts && (
          <td className="border-r border-border px-1 py-0.5 align-middle text-right font-mono text-muted-foreground text-[11px]" data-testid={`line-mqty-${index}`}>
            {line.monthly_qty ?? "—"} /mo
          </td>
        )}
        <td className="px-1 py-0.5 align-middle text-center">
          <button
            type="button"
            onClick={onRemove}
            className="text-destructive hover:opacity-80"
            title="Remove line"
            data-testid={`line-del-${index}`}
          >
            <Trash2 className="w-3 h-3" />
          </button>
        </td>
      </tr>
      {expanded && (
        <tr className="bg-muted/10 border-b border-border" data-testid={`pkg-line-skills-${index}`}>
          <td className="border-r border-border" colSpan={2}></td>
          <td colSpan={colCount - 2} className="px-2 py-2">
            <div className="flex items-center gap-2 mb-1">
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
                Skill breakdown (per unit)
              </Label>
              {autoEffort && (
                <span className="text-[10px] text-amber-700">Effort/u rolls up to <strong>{rolledUp}h</strong></span>
              )}
              <div className="ml-auto">
                <Button type="button" size="sm" variant="outline" className="h-6 rounded-none text-[10px]" onClick={addSkill} data-testid={`line-skill-add-${index}`}>
                  <Plus className="w-3 h-3 mr-1" /> Add skill
                </Button>
              </div>
            </div>
            {skills.length === 0 ? (
              <div className="text-[10px] text-muted-foreground italic">No skills added — effort/u is manually entered.</div>
            ) : (
              <table className="w-full text-[11px] border border-border border-collapse">
                <thead>
                  <tr className="bg-muted/30 text-[10px] uppercase tracking-widest text-muted-foreground">
                    <th className="text-left px-2 py-1 w-56 border-r border-border">Designation</th>
                    <th className="text-right px-2 py-1 w-32 border-r border-border">Hours</th>
                    <th className="text-left px-2 py-1 border-r border-border">Default assignee</th>
                    <th className="w-6"></th>
                  </tr>
                </thead>
                <tbody>
                  {skills.map((s, si) => {
                    // R88 · If the activity master row referenced a designation
                    // that is NOT in the workspace's designation dictionary,
                    // Radix Select would render the trigger blank even though
                    // the value is set. Merge the current value into the
                    // options list (with a "unknown" tag) so nothing gets
                    // silently dropped when importing from activity master.
                    const currentDesig = (s?.designation || "").trim();
                    const optionList = currentDesig && !designations.includes(currentDesig)
                      ? [currentDesig, ...designations]
                      : designations;
                    return (
                    <tr key={si} data-testid={`line-skill-${index}-${si}`} className="border-b border-border">
                      <td className="border-r border-border px-1 py-0.5">
                        <Select value={currentDesig || "_pick"} onValueChange={(v) => setSkill(si, { designation: v === "_pick" ? "" : v })}>
                          <SelectTrigger className="h-6 rounded-none text-[10px] border-transparent focus:border-input" data-testid={`line-skill-designation-${index}-${si}`}>
                            <SelectValue placeholder="Choose designation…" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="_pick">Choose designation…</SelectItem>
                            {optionList.map((d) => (
                              <SelectItem key={d} value={d}>
                                {d}
                                {!designations.includes(d) && (
                                  <span className="text-[9px] text-muted-foreground ml-1">(not in dictionary)</span>
                                )}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="border-r border-border px-1 py-0.5 text-right">
                        <div className="flex items-center gap-0.5 justify-end">
                          <Input type="number" min={0} step={0.25} value={s.hours ?? 0} onChange={(e) => setSkill(si, { hours: e.target.value })}
                                 className="h-6 rounded-none text-[10px] text-right border-transparent focus:border-input"
                                 data-testid={`line-skill-hours-${index}-${si}`} />
                          {/* Feb 2026 · Baseline drift chip. Compare
                              this skill's hours to the activity-master
                              baseline for the same designation. If
                              the drift is > tolerance, show the
                              baseline value with a rose colour and a
                              one-tap Snap button that resets to the
                              baseline. */}
                          {(() => {
                            const act = activityIndex.get(line.activity_type_id);
                            const sb = act?.skill_breakdown || act?.default_skill_breakdown || [];
                            const match = sb.find((x) => (x.designation || "").trim() === (s.designation || "").trim());
                            const baseline = Number(match?.hours || 0);
                            if (!baseline) return null;
                            const val = Number(s.hours) || 0;
                            const tol = Math.max(5, Number(effortTolerancePct) || 50) / 100;
                            const drift = baseline > 0 ? (val - baseline) / baseline : 0;
                            const outside = Math.abs(drift) > tol;
                            if (val === baseline) return null;
                            const tone = outside
                              ? "text-rose-700 border-rose-300 hover:bg-rose-50"
                              : "text-muted-foreground border-border hover:bg-background";
                            return (
                              <button type="button"
                                      onClick={() => setSkill(si, { hours: baseline })}
                                      title={`Baseline is ${baseline}h — click to snap. Drift ${drift > 0 ? "+" : ""}${Math.round(drift * 100)}%`}
                                      className={`shrink-0 text-[9px] font-mono border px-1 py-0.5 ${tone}`}
                                      data-testid={`line-skill-baseline-snap-${index}-${si}`}>
                                ~{baseline}h
                              </button>
                            );
                          })()}
                          {/* Push the new hours out to tasks already spawned. */}
                          {line.id && (s.designation || "").trim() && onSkillHoursBackfill && (
                            <button type="button"
                                    onClick={() => onSkillHoursBackfill(line.id, s.designation.trim(), Number(s.hours) || 0)}
                                    title="Push these hours to existing tasks"
                                    className="shrink-0 text-primary/70 hover:text-primary border border-transparent hover:border-primary/40 p-0.5"
                                    data-testid={`line-skill-hours-push-${index}-${si}`}>
                              <RotateCw className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="border-r border-border px-1 py-0.5">
                        <SkillAssigneePicker
                          value={s.assignee_id || null}
                          members={members}
                          onChange={(uid) => setSkill(si, { assignee_id: uid })}
                          testid={`line-skill-assignee-${index}-${si}`}
                        />
                      </td>
                      <td className="px-1 py-0.5 text-center">
                        <button type="button" onClick={() => removeSkill(si)} className="text-destructive hover:opacity-80" data-testid={`line-skill-del-${index}-${si}`} title="Remove skill row">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

// Ensure the label constant is retained for the Ungrouped fallback header.
export { NO_SECTION_ID };

/**
 * R84 · Searchable, sorted default-assignee picker for a skill breakdown row.
 * Renders as a compact popover trigger showing the current member's name (or
 * "— unassigned —"). Members are sorted A→Z by display name; cmdk drives the
 * live filter. Selecting the current value clears it, so the row can be
 * un-assigned with a second tap.
 */
function SkillAssigneePicker({ value, members, onChange, testid }) {
  const [open, setOpen] = useState(false);
  // Feb 2026 · Only active workspace members are selectable. Existing
  // assignments on inactive/pending members are preserved — the current
  // value is force-included at the top of the list (badged "inactive")
  // so users don't disappear from legacy rows. New picks are restricted
  // to active members only. Sorted alphabetically by name/email.
  const { sorted, currentInactive } = useMemo(() => {
    const arr = Array.isArray(members) ? [...members] : [];
    const activeOnly = arr.filter((m) => (m.status || "active") === "active");
    activeOnly.sort((a, b) => (a.name || a.email || "").localeCompare(b.name || b.email || ""));
    const inactiveMatch = value && !activeOnly.some((m) => m.user_id === value)
      ? arr.find((m) => m.user_id === value)
      : null;
    return { sorted: activeOnly, currentInactive: inactiveMatch };
  }, [members, value]);
  const activeCurrent = sorted.find((m) => m.user_id === value);
  const current = activeCurrent || currentInactive;
  const label = current ? (current.name || current.email || "Unknown") : "— unassigned —";
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-6 w-full justify-between rounded-none text-[10px] border border-transparent hover:border-input px-2"
          data-testid={testid}
        >
          <span className={`truncate ${current ? "" : "text-muted-foreground italic"}`}>
            {label}
            {currentInactive && !activeCurrent && (
              <span className="ml-1 text-[8px] uppercase tracking-widest text-amber-700 bg-amber-100 px-1 py-0.5 border border-amber-300">
                inactive
              </span>
            )}
          </span>
          <ChevronsUpDown className="w-3 h-3 opacity-50 shrink-0 ml-1" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0 rounded-none" align="start">
        <Command>
          <CommandInput placeholder="Search active members…" className="text-xs" data-testid={`${testid}-search`} />
          <CommandList>
            <CommandEmpty>No active member matches.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                key="__unassigned__"
                value="unassigned"
                onSelect={() => { onChange(null); setOpen(false); }}
                className="text-xs italic text-muted-foreground"
                data-testid={`${testid}-option-none`}
              >
                — unassigned —
              </CommandItem>
              {/* Current inactive assignee — kept so the picker reflects
                   historical state. Picking it is a no-op (re-selects the
                   same value) but it's visible for context. */}
              {currentInactive && !activeCurrent && (
                <CommandItem
                  key={`__inactive_${currentInactive.user_id}`}
                  value={`${currentInactive.name || ""} ${currentInactive.email || ""} inactive`}
                  onSelect={() => { setOpen(false); }}
                  className="text-xs opacity-70"
                  data-testid={`${testid}-option-inactive-${currentInactive.user_id}`}
                >
                  <div className="flex-1 truncate">
                    <div className="font-medium truncate">{currentInactive.name || currentInactive.email || "Unknown"}</div>
                    <div className="text-[10px] text-amber-700">Inactive · kept from legacy assignment</div>
                  </div>
                  <span className="text-[10px] text-primary ml-2">✓</span>
                </CommandItem>
              )}
              {sorted.map((m) => (
                <CommandItem
                  key={m.user_id}
                  value={`${m.name || ""} ${m.email || ""}`}
                  onSelect={() => { onChange(m.user_id === value ? null : m.user_id); setOpen(false); }}
                  className="text-xs"
                  data-testid={`${testid}-option-${m.user_id}`}
                >
                  <div className="flex-1 truncate">
                    <div className="font-medium truncate">{m.name || m.email || "Unknown"}</div>
                    {m.email && m.name && (
                      <div className="text-[10px] text-muted-foreground truncate">{m.email}</div>
                    )}
                  </div>
                  {m.user_id === value && <span className="text-[10px] text-primary ml-2">✓</span>}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
